import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String, urls: Array, interval: Number }

  connect() {
    // Default to 60 seconds (60000ms) if no interval specified
    this.intervalValue = this.intervalValue || 60000

    // `rotationIndex` tracks the position CURRENTLY on screen, not "next to
    // fetch" -- it starts at 0 to match the server-rendered initial view
    // (index 0 of `urls`, when present). The timer tick and the manual
    // next()/previous() actions all move it first, then fetch+render
    // whatever it now points at.
    this.rotationIndex = 0

    this.startRefresh()
  }

  disconnect() {
    this.stopRefresh()
  }

  startRefresh() {
    this.stopRefresh()
    this.refreshTimer = setInterval(() => {
      this.refresh()
    }, this.intervalValue)
  }

  stopRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  // Timer tick: advance one step forward and render. If a `urls` array is
  // provided, this rotates round-robin between them (e.g. a "current month"
  // and "previous month" view of the same dashboard) instead of always
  // re-fetching the same single URL. Falls back to the existing single-url
  // behaviour exactly as before when no urls array is set -- every other
  // dashboard sharing this controller keeps working unchanged.
  refresh() {
    this.fetchAndRender(this.advance(1))
  }

  // Manual controls -- let a viewer standing at the kiosk jump between
  // rotation views on demand instead of waiting for the next auto-tick.
  // Each restarts the auto-refresh timer so a manual click "owns" the next
  // full interval, rather than an auto-tick landing back on the same/next
  // view a moment later.
  next() {
    this.fetchAndRender(this.advance(1))
    this.startRefresh()
  }

  previous() {
    this.fetchAndRender(this.advance(-1))
    this.startRefresh()
  }

  // Moves the rotation index by `step` (wrapping correctly for negative
  // steps, e.g. -1 from position 0) and returns the URL now at that
  // position. Falls back to the single `urlValue` when no `urls` array is
  // set, leaving pages without a `urls` value completely unaffected.
  advance(step) {
    if (this.hasUrlsValue && this.urlsValue.length > 0) {
      const length = this.urlsValue.length
      this.rotationIndex = ((this.rotationIndex + step) % length + length) % length
      return this.urlsValue[this.rotationIndex]
    }

    return this.urlValue
  }

  // Shared fetch + turbo-stream render, used by both the timer tick
  // (refresh()) and the manual next()/previous() actions.
  async fetchAndRender(url) {
    if (!url) return

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      console.error('Dashboard refresh failed:', error)
    }
  }
}
